import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Clock,
  Eye,
  Loader2,
  MailCheck,
  MessageSquareReply,
  MousePointerClick,
  Pause,
  Play,
  Plus,
  Rocket,
  Send,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { type AttachmentRow } from '@/api/attachments';
import { campaignsApi, type CampaignDetail, type CampaignStep, type ReplyEngine } from '@/api/campaigns';
import { leadListsApi } from '@/api/leads';
import { mailboxesApi, mailboxPoolsApi } from '@/api/mailboxes';
import { AttachmentPicker } from '@/components/attachment-picker';
import { CsvMapper, type MappedRow } from '@/components/csv-mapper';
import { RichTextEditor } from '@/components/rich-text-editor';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Field } from '@/components/ui/field';
import { KpiCard } from '@/components/ui/kpi-card';
import { Loader } from '@/components/ui/loader';
import { Modal } from '@/components/ui/modal';
import { StatusPill } from '@/components/ui/status-pill';
import { Topbar } from '@/components/ui/topbar';
import { ApiError } from '@/lib/api-client';
import { qk } from '@/lib/query-keys';

export function CampaignsListPage(): JSX.Element {
  const [createOpen, setCreateOpen] = useState(false);
  const list = useQuery({
    queryKey: qk.campaigns.all,
    queryFn: () => campaignsApi.list(),
  });

  const campaigns = list.data ?? [];

  return (
    <>
      <Topbar
        title="Campaigns"
        description={`${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}`}
        actions={
          <button className="mb-btn-primary" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" strokeWidth={2} />
            New campaign
          </button>
        }
      />
      <div className="flex-1 overflow-auto p-6">
        {list.isLoading ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : list.isError ? (
          <ErrorState
            title="Couldn't load campaigns"
            onRetry={() => void list.refetch()}
          />
        ) : campaigns.length === 0 ? (
          <EmptyState
            icon={Send}
            title="No campaigns yet"
            description="Create a campaign to start sending outbound emails."
          />
        ) : (
          <div className="mb-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-mb-surface">
                <tr className="text-left text-xs uppercase tracking-wider text-mb-text-muted">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Completion</th>
                  <th className="px-3 py-2 text-right">Sent</th>
                  <th className="px-3 py-2 text-right">Open %</th>
                  <th className="px-3 py-2 text-right">Click %</th>
                  <th className="px-3 py-2 text-right">Reply %</th>
                  <th className="px-3 py-2 text-right">Bounce %</th>
                  <th className="px-4 py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => {
                  const s = c.stats;
                  const pct = (n: number) =>
                    s && s.sent > 0 ? ((n / s.sent) * 100).toFixed(1) + '%' : '—';
                  const completion =
                    s && s.total > 0
                      ? ((s.sent / s.total) * 100).toFixed(1) + '%'
                      : '—';
                  return (
                    <tr
                      key={c.id}
                      className="border-t border-mb-border hover:bg-mb-surface-hover"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/campaigns/${c.id}`}
                          className="font-medium text-mb-text hover:text-mb-accent-press"
                        >
                          {c.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill status={c.status} />
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-mb-text">
                        {completion}
                        {s ? (
                          <div className="text-[10px] text-mb-text-subtle">
                            {s.sent.toLocaleString()} / {s.total.toLocaleString()}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-mb-text">
                        {s ? s.sent.toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-mb-text">
                        {pct(s?.opened ?? 0)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-mb-text">
                        {pct(s?.clicked ?? 0)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-mb-text">
                        {pct(s?.replied ?? 0)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-mb-text">
                        {pct(s?.bounced ?? 0)}
                      </td>
                      <td className="px-4 py-3 text-mb-text-muted">
                        {new Date(c.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateCampaignModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}

interface StepDraft {
  subject: string;
  body: string;
  delay: number;
  attachments: AttachmentRow[];
}

type CsvRow = MappedRow;

// Send-day bits — bit 0 = Mon ... bit 6 = Sun. Default is Mon-Fri (31).
const DAY_LABELS: ReadonlyArray<{ bit: number; short: string; full: string }> = [
  { bit: 1, short: 'Mon', full: 'Monday' },
  { bit: 2, short: 'Tue', full: 'Tuesday' },
  { bit: 4, short: 'Wed', full: 'Wednesday' },
  { bit: 8, short: 'Thu', full: 'Thursday' },
  { bit: 16, short: 'Fri', full: 'Friday' },
  { bit: 32, short: 'Sat', full: 'Saturday' },
  { bit: 64, short: 'Sun', full: 'Sunday' },
];

const TIMEZONES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'America/Chicago', label: 'Central — Chicago (CT)' },
  { id: 'America/New_York', label: 'Eastern — New York (ET)' },
  { id: 'America/Denver', label: 'Mountain — Denver (MT)' },
  { id: 'America/Phoenix', label: 'Arizona (no DST)' },
  { id: 'America/Los_Angeles', label: 'Pacific — Los Angeles (PT)' },
  { id: 'America/Anchorage', label: 'Alaska (AKT)' },
  { id: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { id: 'UTC', label: 'UTC' },
];

function daysLabel(bits: number): string {
  const on = DAY_LABELS.filter((d) => (bits & d.bit) !== 0).map((d) => d.short);
  if (on.length === 0) return 'No days selected';
  if (on.length === 7) return 'Every day';
  if (bits === 31) return 'Mon-Fri';
  if (bits === 63) return 'Mon-Sat';
  return on.join(', ');
}

/**
 * Convert a wall-clock "YYYY-MM-DDTHH:mm" (as typed by the operator) into a
 * UTC ISO string, interpreting the clock as if it were in `tz`. We iterate
 * once via Intl.formatToParts to find the offset — no external date lib
 * needed. Returns null when the input is empty.
 */
function wallClockToUtcIso(local: string, tz: string): string | null {
  if (!local) return null;
  const [d, t] = local.split('T');
  if (!d || !t) return null;
  const [y, mo, day] = d.split('-').map(Number);
  const [h, mi] = t.split(':').map(Number);
  const utcGuess = Date.UTC(y, mo - 1, day, h, mi);
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date(utcGuess));
  const pick = (k: string): number =>
    Number(parts.find((p) => p.type === k)?.value ?? 0);
  const seenHour = pick('hour') === 24 ? 0 : pick('hour');
  const seen = Date.UTC(pick('year'), pick('month') - 1, pick('day'), seenHour, pick('minute'));
  const offset = seen - utcGuess;
  return new Date(utcGuess - offset).toISOString();
}

function formatInZone(iso: string | null, tz: string): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
}

function CreateCampaignModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [stage, setStage] = useState<1 | 2 | 3 | 4>(1);
  const [name, setName] = useState('');

  // Lead source: pick an existing list OR upload a CSV that we'll turn into
  // a new list transparently. Only one is populated at a time.
  const [sourceMode, setSourceMode] = useState<'existing' | 'csv'>('existing');
  const [leadListId, setLeadListId] = useState<string>('');
  const [csvRows, setCsvRows] = useState<CsvRow[]>([]);

  // Multi-select — sends will round-robin across these mailboxes. Follow-up
  // messages to a given lead pin to the mailbox that sent step 1 (enforced
  // by the follow-up scheduler on the backend).
  const [mailboxIds, setMailboxIds] = useState<string[]>([]);
  const [replyEngine, setReplyEngine] = useState<ReplyEngine>('mlm');
  const [steps, setSteps] = useState<StepDraft[]>([
    { subject: 'Hi {{first_name}}', body: 'Wanted to reach out about {{company}}…', delay: 0, attachments: [] },
  ]);

  // Schedule defaults per policy: Mon-Fri, 08:00-17:00, America/Chicago.
  // `startAt` is a wall-clock string ('YYYY-MM-DDTHH:mm') in the chosen tz;
  // empty means "start as soon as I hit Launch".
  const [sendDays, setSendDays] = useState<number>(31);
  const [sendWindowStart, setSendWindowStart] = useState<string>('08:00');
  const [sendWindowEnd, setSendWindowEnd] = useState<string>('17:00');
  const [timezone, setTimezone] = useState<string>('America/Chicago');
  const [startAt, setStartAt] = useState<string>('');
  // Per-campaign daily send ceiling. Empty string = no campaign-level cap.
  const [dailyLimit, setDailyLimit] = useState<string>('');

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [previewStepIdx, setPreviewStepIdx] = useState<number | null>(null);

  const leadLists = useQuery({
    queryKey: qk.leadLists.all,
    queryFn: () => leadListsApi.list(),
    enabled: open,
  });
  const mailboxes = useQuery({
    queryKey: qk.mailboxes.all,
    queryFn: () => mailboxesApi.list(),
    enabled: open,
  });

  const activeMailboxes = (mailboxes.data ?? []).filter((m) => m.status === 'active');
  const pickedList = (leadLists.data ?? []).find((l) => l.id === leadListId) ?? null;

  const create = useMutation({
    mutationFn: async () => {
      // If the operator uploaded a CSV, spin up a fresh list + import the
      // rows first. Backend takes care of dedup on (list_id, email).
      let resolvedListId = leadListId;
      if (sourceMode === 'csv') {
        const newList = await leadListsApi.create({
          name: `${name} — leads`,
          description: `Auto-created from campaign wizard (${csvRows.length} leads uploaded)`,
        });
        await leadListsApi.import(newList.id, csvRows);
        resolvedListId = newList.id;
      }

      // Auto-create a mailbox pool for this campaign so operators don't
      // have to manage pools separately. Round-robin across the selected
      // mailboxes happens at launch time; follow-ups to a lead pin to the
      // mailbox that sent step 1.
      const pool = await mailboxPoolsApi.create({
        name: `${name} — pool`,
        mailbox_ids: mailboxIds,
      });
      return campaignsApi.create({
        name,
        lead_list_id: resolvedListId,
        from_mailbox_pool_id: pool.id,
        reply_engine: replyEngine,
        send_days: sendDays,
        send_window_start: sendWindowStart,
        send_window_end: sendWindowEnd,
        timezone,
        scheduled_start_at: wallClockToUtcIso(startAt, timezone),
        daily_send_limit: dailyLimit.trim() ? Math.max(1, parseInt(dailyLimit, 10)) : null,
        steps: steps.map((s, i) => ({
          step_number: i + 1,
          subject_template: s.subject,
          body_html_template: s.body,
          delay_days_after_prev: s.delay,
          stop_on_reply: true,
          attachment_ids: s.attachments.map((a) => a.id),
        })),
      });
    },
    onSuccess: async (camp) => {
      await qc.invalidateQueries({ queryKey: qk.campaigns.all });
      await qc.invalidateQueries({ queryKey: qk.leadLists.all });
      onClose();
      navigate(`/campaigns/${camp.id}`);
    },
  });

  function resetAndClose() {
    setStage(1);
    setName('');
    setSourceMode('existing');
    setLeadListId('');
    setCsvRows([]);
    setMailboxIds([]);
    setReplyEngine('mlm');
    setSteps([{ subject: 'Hi {{first_name}}', body: 'Wanted to reach out about {{company}}…', delay: 0, attachments: [] }]);
    setSendDays(31);
    setSendWindowStart('08:00');
    setSendWindowEnd('17:00');
    setTimezone('America/Chicago');
    setStartAt('');
    setDailyLimit('');
    setSubmitError(null);
    setPreviewStepIdx(null);
    onClose();
  }

  const scheduleReady =
    sendDays > 0 &&
    sendWindowStart < sendWindowEnd &&
    /^\d{2}:\d{2}$/.test(sendWindowStart) &&
    /^\d{2}:\d{2}$/.test(sendWindowEnd);

  const audienceReady =
    sourceMode === 'existing' ? Boolean(leadListId) : csvRows.length > 0;

  async function submit() {
    setSubmitError(null);
    if (!name || mailboxIds.length === 0 || !audienceReady) {
      setSubmitError('Fill in every required field before launching.');
      return;
    }
    if (!steps[0]?.subject) {
      setSubmitError('Step 1 needs a subject line — follow-ups thread under it.');
      return;
    }
    if (steps.some((s) => !s.body)) {
      setSubmitError('Every step needs a body.');
      return;
    }
    if (!scheduleReady) {
      setSubmitError('Pick at least one send day and a valid window.');
      return;
    }
    try {
      await create.mutateAsync();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Could not create campaign');
    }
  }

  return (
    <Modal open={open} onClose={resetAndClose} title={`New campaign — step ${stage} of 4`} size="lg">
      {/* Stage stepper */}
      <div className="flex gap-2 mb-4">
        {[1, 2, 3, 4].map((n) => (
          <div
            key={n}
            className={
              'flex-1 h-1 rounded ' +
              (stage >= n ? 'bg-mb-accent' : 'bg-mb-border')
            }
          />
        ))}
      </div>

      {stage === 1 ? (
        <div className="space-y-3">
          <Field label="Campaign name" required>
            <input
              type="text"
              className="mb-input"
              placeholder="e.g. Q3 KC Plumbers outreach"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <div>
            <label className="block text-xs font-medium text-mb-text uppercase tracking-wider mb-1.5">
              Audience <span className="text-mb-danger">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button
                type="button"
                onClick={() => setSourceMode('existing')}
                className={
                  'text-left rounded-lg border p-2.5 text-xs transition-colors ' +
                  (sourceMode === 'existing'
                    ? 'border-mb-accent bg-mb-accent-soft'
                    : 'border-mb-border hover:border-mb-text-muted')
                }
              >
                <div className="font-medium text-mb-text">Select existing list</div>
                <div className="mt-0.5 text-mb-text-muted">Reuse leads you already imported</div>
              </button>
              <button
                type="button"
                onClick={() => setSourceMode('csv')}
                className={
                  'text-left rounded-lg border p-2.5 text-xs transition-colors ' +
                  (sourceMode === 'csv'
                    ? 'border-mb-accent bg-mb-accent-soft'
                    : 'border-mb-border hover:border-mb-text-muted')
                }
              >
                <div className="font-medium text-mb-text">Upload CSV</div>
                <div className="mt-0.5 text-mb-text-muted">Auto-creates a list named after this campaign</div>
              </button>
            </div>

            {sourceMode === 'existing' ? (
              <>
                <select
                  value={leadListId}
                  onChange={(e) => setLeadListId(e.target.value)}
                  className="mb-input"
                >
                  <option value="">Select a lead list…</option>
                  {(leadLists.data ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} ({l.total_leads} leads)
                    </option>
                  ))}
                </select>
                {pickedList ? (
                  <p className="text-xs text-mb-text-muted mt-1">
                    {pickedList.total_leads} leads in this list
                  </p>
                ) : null}
              </>
            ) : csvRows.length > 0 ? (
              <div className="rounded-md border border-mb-success/30 bg-mb-success-soft p-2 text-xs">
                <b>{csvRows.length}</b> leads ready to import — first row:{' '}
                <span className="font-mono">{csvRows[0]?.email}</span>
                <button
                  type="button"
                  className="ml-3 text-mb-accent-press hover:underline"
                  onClick={() => setCsvRows([])}
                >
                  clear
                </button>
              </div>
            ) : (
              <CsvMapper
                confirmLabel="Use these leads"
                onConfirm={(rows) => setCsvRows(rows as CsvRow[])}
              />
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-mb-text uppercase tracking-wider mb-1.5">
              Send from <span className="text-mb-danger">*</span>
            </label>
            {activeMailboxes.length === 0 ? (
              <p className="text-xs text-mb-text-muted">
                Connect a mailbox at <Link to="/mailboxes" className="text-mb-accent-press hover:underline">/mailboxes</Link> first.
              </p>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1.5 text-xs">
                  <button
                    type="button"
                    onClick={() => setMailboxIds(activeMailboxes.map((m) => m.id))}
                    className="text-mb-accent-press hover:underline"
                  >
                    Select all ({activeMailboxes.length})
                  </button>
                  <span className="text-mb-text-subtle">·</span>
                  <button
                    type="button"
                    onClick={() => setMailboxIds([])}
                    className="text-mb-text-muted hover:underline"
                  >
                    Clear
                  </button>
                  <span className="ml-auto text-mb-text-muted">
                    {mailboxIds.length} selected
                  </span>
                </div>
                <div className="rounded-md border border-mb-border bg-mb-surface max-h-40 overflow-y-auto">
                  {activeMailboxes.map((m) => {
                    const on = mailboxIds.includes(m.id);
                    return (
                      <label
                        key={m.id}
                        className={
                          'flex items-center gap-2 border-b border-mb-border px-3 py-2 text-sm cursor-pointer last:border-b-0 ' +
                          (on ? 'bg-mb-accent-soft' : 'hover:bg-mb-bg-soft')
                        }
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setMailboxIds([...mailboxIds, m.id]);
                            } else {
                              setMailboxIds(mailboxIds.filter((id) => id !== m.id));
                            }
                          }}
                          className="h-4 w-4"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-mb-text truncate">
                            {m.display_name ? `${m.display_name} <${m.email}>` : m.email}
                          </div>
                          <div className="text-[10px] text-mb-text-subtle">
                            {m.provider} · {m.daily_send_cap}/day · {m.hourly_send_cap}/hr
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-mb-text-muted mt-1">
                  Sends will round-robin across the selected mailboxes. Follow-ups
                  to a given lead always come from the same mailbox that sent step 1.
                </p>
              </>
            )}
          </div>
        </div>
      ) : null}

      {stage === 2 ? (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-mb-text uppercase tracking-wider mb-2">
              Reply engine
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'mlm' as const, label: 'MLM (deterministic)', hint: 'Free. Keyword-matched replies, ships provenance.' },
                { id: 'deepseek' as const, label: 'Markdown Marty (DeepSeek)', hint: 'LLM. Best for nuanced replies. Uses tenant DeepSeek key.' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setReplyEngine(opt.id)}
                  className={
                    'text-left rounded-lg border p-3 transition-colors ' +
                    (replyEngine === opt.id
                      ? 'border-mb-accent bg-mb-accent-soft'
                      : 'border-mb-border hover:border-mb-text-muted')
                  }
                >
                  <div className="font-medium text-mb-text">{opt.label}</div>
                  <div className="mt-1 text-xs text-mb-text-muted">{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-mb-text uppercase tracking-wider">
                Sequence — {steps.length} step{steps.length === 1 ? '' : 's'}
              </label>
              <button
                type="button"
                onClick={() => setSteps([...steps, { subject: '', body: '', delay: 3, attachments: [] }])}
                disabled={steps.length >= 10}
                className="text-xs text-mb-accent-press hover:underline disabled:text-mb-text-muted disabled:no-underline"
              >
                + Add step
              </button>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {steps.map((s, i) => (
                <div key={i} className="rounded-md border border-mb-border p-3 bg-mb-surface">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-medium text-mb-text-muted">
                      Step {i + 1}
                      {i > 0 ? (
                        <span className="ml-2">
                          · delay
                          <input
                            type="number"
                            min={0}
                            max={60}
                            value={s.delay}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              setSteps(steps.map((ss, j) => (j === i ? { ...ss, delay: v } : ss)));
                            }}
                            className="mx-1 w-14 rounded border border-mb-border px-1 text-mb-text"
                          />
                          days after step {i}
                        </span>
                      ) : (
                        <span className="ml-2 text-mb-text-subtle">sends first</span>
                      )}
                    </div>
                    {steps.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => setSteps(steps.filter((_, j) => j !== i))}
                        className="text-mb-text-muted hover:text-mb-danger"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                  {i === 0 ? (
                    <input
                      type="text"
                      className="mb-input mb-2"
                      placeholder="Subject line — {{first_name}}, {{company}} allowed"
                      value={s.subject}
                      onChange={(e) => setSteps(steps.map((ss, j) => (j === i ? { ...ss, subject: e.target.value } : ss)))}
                    />
                  ) : (
                    <div className="mb-2 rounded border border-dashed border-mb-border bg-mb-bg-soft px-2.5 py-2 text-[11px] text-mb-text-muted">
                      Sends as a reply under step 1's subject:{' '}
                      <b className="text-mb-text">
                        Re: {steps[0]?.subject || '(fill in step 1 subject first)'}
                      </b>
                      <div className="mt-0.5 text-mb-text-subtle">
                        Threading headers land in the same conversation in the
                        recipient's inbox — no need for a fresh subject line.
                      </div>
                    </div>
                  )}
                  <RichTextEditor
                    value={s.body}
                    onChange={(html) =>
                      setSteps(steps.map((ss, j) => (j === i ? { ...ss, body: html } : ss)))
                    }
                    placeholder="Body — Use {{first_name}} {{last_name}} {{company}} {{title}} {{description}} or any custom field."
                    rows={5}
                  />
                  <div className="mt-2">
                    <AttachmentPicker
                      attached={s.attachments}
                      onChange={(next) =>
                        setSteps(steps.map((ss, j) => (j === i ? { ...ss, attachments: next } : ss)))
                      }
                    />
                  </div>
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      className="text-xs text-mb-accent-press hover:underline flex items-center gap-1"
                      onClick={() => setPreviewStepIdx(i)}
                      disabled={!s.body}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      Preview step {i + 1}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {stage === 3 ? (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-mb-text uppercase tracking-wider mb-2">
              Days to send
            </label>
            <div className="flex flex-wrap gap-1.5">
              {DAY_LABELS.map((d) => {
                const on = (sendDays & d.bit) !== 0;
                return (
                  <button
                    key={d.bit}
                    type="button"
                    onClick={() => setSendDays(sendDays ^ d.bit)}
                    className={
                      'rounded-md border px-3 py-1.5 text-xs transition-colors ' +
                      (on
                        ? 'border-mb-accent bg-mb-accent-soft text-mb-accent-press font-medium'
                        : 'border-mb-border bg-mb-surface text-mb-text-muted hover:border-mb-text-muted')
                    }
                  >
                    {d.short}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-mb-text-muted mt-1.5">
              Default is Mon-Fri. Sunday sends are blocked platform-wide even if you toggle it on.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Window starts" hint="Local to campaign timezone">
              <input
                type="time"
                className="mb-input"
                value={sendWindowStart}
                onChange={(e) => setSendWindowStart(e.target.value)}
              />
            </Field>
            <Field label="Window ends" hint="Nothing sends after this">
              <input
                type="time"
                className="mb-input"
                value={sendWindowEnd}
                onChange={(e) => setSendWindowEnd(e.target.value)}
              />
            </Field>
          </div>

          <Field label="Timezone" hint="Send window + start time are interpreted in this zone">
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="mb-input"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.id} value={tz.id}>
                  {tz.label}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Start date & time (optional)"
            hint="Leave blank to start as soon as you click Launch. Otherwise the first message goes out on/after this moment."
          >
            <input
              type="datetime-local"
              className="mb-input"
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
            />
          </Field>

          <Field
            label="Daily send limit (optional)"
            hint="Cap how many emails this campaign sends per day. Blank = no campaign-level cap (mailbox caps still apply)."
          >
            <input
              type="number"
              min={1}
              max={100000}
              placeholder="e.g. 100"
              className="mb-input"
              value={dailyLimit}
              onChange={(e) => setDailyLimit(e.target.value)}
            />
          </Field>

          {sendWindowStart >= sendWindowEnd ? (
            <div role="alert" className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
              Window end must be later than the start.
            </div>
          ) : null}

          <div className="rounded-md border border-mb-border bg-mb-surface p-3 text-xs text-mb-text-muted">
            <div className="flex items-center gap-1.5 text-mb-text mb-1">
              <Clock className="h-3.5 w-3.5" />
              <span className="font-medium">Recap</span>
            </div>
            Sending on <b>{daysLabel(sendDays)}</b> between{' '}
            <b>{sendWindowStart}</b> and <b>{sendWindowEnd}</b> ({timezone})
            {startAt ? <>, starting <b>{formatInZone(wallClockToUtcIso(startAt, timezone), timezone)}</b></> : <>, starting <b>immediately on launch</b></>}.
          </div>
        </div>
      ) : null}

      {stage === 4 ? (
        <div className="space-y-3">
          <div className="rounded-md border border-mb-border bg-mb-surface p-4">
            <div className="font-medium text-mb-text mb-3">Review</div>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-mb-text-muted">Name</dt><dd className="text-mb-text">{name}</dd>
              <dt className="text-mb-text-muted">Audience</dt>
              <dd className="text-mb-text">
                {sourceMode === 'existing'
                  ? <>{pickedList?.name} — <b>{pickedList?.total_leads}</b> leads</>
                  : <><b>{csvRows.length}</b> leads (new list will be created: "{name} — leads")</>}
              </dd>
              <dt className="text-mb-text-muted">From mailboxes</dt>
              <dd className="text-mb-text">
                {mailboxIds.length === 1
                  ? activeMailboxes.find((m) => m.id === mailboxIds[0])?.email
                  : <><b>{mailboxIds.length}</b> mailboxes (round-robin)</>}
                {mailboxIds.length > 1 ? (
                  <ul className="mt-1 text-xs text-mb-text-muted list-disc list-inside">
                    {mailboxIds.map((id) => (
                      <li key={id}>{activeMailboxes.find((m) => m.id === id)?.email}</li>
                    ))}
                  </ul>
                ) : null}
              </dd>
              <dt className="text-mb-text-muted">Reply engine</dt>
              <dd className="text-mb-text">{replyEngine === 'mlm' ? 'MLM (free)' : 'Markdown Marty (DeepSeek)'}</dd>
              <dt className="text-mb-text-muted">Sequence</dt>
              <dd className="text-mb-text">{steps.length} step{steps.length === 1 ? '' : 's'}</dd>
              <dt className="text-mb-text-muted">Send days</dt>
              <dd className="text-mb-text">{daysLabel(sendDays)}</dd>
              <dt className="text-mb-text-muted">Send window</dt>
              <dd className="text-mb-text">{sendWindowStart} – {sendWindowEnd} ({timezone})</dd>
              <dt className="text-mb-text-muted">Starts</dt>
              <dd className="text-mb-text">
                {startAt
                  ? formatInZone(wallClockToUtcIso(startAt, timezone), timezone) + ` (${timezone})`
                  : 'Immediately on Launch'}
              </dd>
            </dl>
          </div>
          <div className="text-xs text-mb-text-muted">
            Campaign is created in <b>draft</b> status. Nothing sends until you hit Launch on the detail page.
          </div>
        </div>
      ) : null}

      {submitError ? (
        <div role="alert" className="mt-3 rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
          {submitError}
        </div>
      ) : null}

      <div className="flex justify-between gap-2 pt-4 mt-4 border-t border-mb-border">
        <button
          type="button"
          className="mb-btn-secondary"
          onClick={() => (stage === 1 ? resetAndClose() : setStage((stage - 1) as 1 | 2 | 3))}
        >
          {stage === 1 ? 'Cancel' : 'Back'}
        </button>
        {stage < 4 ? (
          <button
            type="button"
            className="mb-btn-primary"
            disabled={
              (stage === 1 && (!name || !audienceReady || mailboxIds.length === 0)) ||
              (stage === 3 && !scheduleReady)
            }
            onClick={() => setStage((stage + 1) as 2 | 3 | 4)}
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            className="mb-btn-primary"
            disabled={create.isPending}
            onClick={submit}
          >
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Create campaign
          </button>
        )}
      </div>

      {previewStepIdx !== null ? (
        <PreviewStepModal
          step={steps[previewStepIdx]}
          stepIndex={previewStepIdx}
          steps={steps}
          fromMailboxEmail={
            activeMailboxes.find((m) => mailboxIds.includes(m.id))?.email ?? null
          }
          leadListId={sourceMode === 'existing' ? leadListId : null}
          csvRows={sourceMode === 'csv' ? csvRows : []}
          onClose={() => setPreviewStepIdx(null)}
        />
      ) : null}
    </Modal>
  );
}

/**
 * Renders the campaign step exactly as it will land in a lead's inbox.
 *
 * Grabs a random lead from either the selected list (via API) or the
 * uploaded CSV rows (in wizard-side state), substitutes {{variables}}
 * in the subject + body templates, and shows subject + rendered HTML
 * body. Follow-up steps use step 1's subject with a "Re:" prefix so the
 * preview matches what the recipient actually sees.
 */
function PreviewStepModal({
  step,
  stepIndex,
  steps,
  fromMailboxEmail,
  leadListId,
  csvRows,
  onClose,
}: {
  step: StepDraft;
  stepIndex: number;
  steps: StepDraft[];
  fromMailboxEmail: string | null;
  leadListId: string | null;
  csvRows: CsvRow[];
  onClose: () => void;
}): JSX.Element {
  const sample = useQuery({
    queryKey: ['preview-sample', leadListId],
    queryFn: async () => {
      if (!leadListId) return null;
      const page = await leadListsApi.leadsIn(leadListId, { page: 1, limit: 1 });
      return page.items[0] ?? null;
    },
    enabled: Boolean(leadListId),
    staleTime: 60_000,
  });

  const vars: Record<string, string> = (() => {
    if (leadListId && sample.data) {
      const l = sample.data;
      const cf = (l.custom_fields ?? {}) as Record<string, unknown>;
      const out: Record<string, string> = {
        first_name: l.first_name ?? '',
        last_name: l.last_name ?? '',
        company: l.company ?? '',
        title: l.title ?? '',
        email: l.email,
        description: l.description ?? '',
        main_vertical: l.main_vertical ?? '',
      };
      for (const [k, v] of Object.entries(cf)) out[k] = String(v ?? '');
      return out;
    }
    // CSV path — use the first parsed row
    if (csvRows.length > 0) {
      const l = csvRows[0];
      const cf = l.custom_fields ?? {};
      return {
        first_name: l.first_name ?? '',
        last_name: l.last_name ?? '',
        company: l.company ?? '',
        title: l.title ?? '',
        email: l.email,
        description: (l as unknown as { description?: string }).description ?? '',
        main_vertical: l.main_vertical ?? '',
        ...cf,
      };
    }
    // No data available — populate with example placeholders so the UI
    // still shows a plausible render.
    return {
      first_name: 'Jane',
      last_name: 'Doe',
      company: 'Acme Corp',
      title: 'VP Sales',
      email: 'jane@example.com',
      description: '',
      main_vertical: '',
    };
  })();

  const rawSubject =
    stepIndex === 0
      ? step.subject
      : `Re: ${steps[0]?.subject ?? ''}`;
  const subject = renderTemplate(rawSubject, vars);
  const body = renderTemplate(step.body, vars);

  const sourceLabel = leadListId
    ? sample.isLoading
      ? 'Loading a sample lead…'
      : sample.data
        ? `Rendered with real lead: ${sample.data.email}`
        : 'List has no leads yet — using placeholders'
    : csvRows.length > 0
      ? `Rendered with first uploaded CSV row: ${csvRows[0].email}`
      : 'Rendered with placeholder data (Jane Doe @ Acme Corp)';

  return (
    <Modal open={true} onClose={onClose} title={`Preview — step ${stepIndex + 1}`} size="lg">
      <div className="space-y-3">
        <div className="rounded-md border border-mb-border bg-mb-bg-soft px-3 py-2 text-[11px] text-mb-text-muted">
          {sourceLabel}
        </div>

        {/* Email envelope */}
        <div className="rounded-md border border-mb-border bg-white overflow-hidden">
          <div className="border-b border-mb-border bg-mb-surface px-4 py-2 text-xs">
            <div>
              <span className="text-mb-text-subtle">From: </span>
              <span className="font-mono text-mb-text">{fromMailboxEmail ?? 'your mailbox'}</span>
            </div>
            <div className="mt-0.5">
              <span className="text-mb-text-subtle">To: </span>
              <span className="font-mono text-mb-text">{vars.email}</span>
            </div>
            <div className="mt-1 text-sm font-medium text-mb-text">
              {subject || <span className="italic text-mb-text-subtle">(no subject)</span>}
            </div>
          </div>
          <div
            className="prose prose-sm max-w-none px-4 py-4 text-sm"
            dangerouslySetInnerHTML={{ __html: body || '<i style="color:#999">(empty body)</i>' }}
          />
        </div>

        <div className="rounded-md border border-mb-border bg-mb-surface p-2 text-[11px] text-mb-text-muted">
          <b>Tip:</b> anything wrapped in <code>{'{{'}braces{'}}'}</code> in the
          template is replaced at send time with that lead's actual value. Empty
          fields render as blank strings.
        </div>
      </div>
    </Modal>
  );
}

/** Client-side clone of the backend template renderer — replaces
 *  {{key}} tokens, drops unresolved ones. */
function renderTemplate(tpl: string, vars: Record<string, string>): string {
  if (!tpl) return '';
  let out = tpl;
  for (const [k, v] of Object.entries(vars)) {
    out = out.split(`{{${k}}}`).join(v ?? '');
  }
  return out.replace(/\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}/g, '');
}

export function CampaignDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: qk.campaigns.detail(id),
    queryFn: () => campaignsApi.detail(id),
    enabled: Boolean(id),
  });

  const launch = useMutation({
    mutationFn: () => campaignsApi.launch(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.campaigns.detail(id) }),
  });
  const pause = useMutation({
    mutationFn: () => campaignsApi.pause(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.campaigns.detail(id) }),
  });

  if (detail.isLoading) {
    return (
      <>
        <Topbar title="Loading…" />
        <div className="flex-1 flex items-center justify-center">
          <Loader />
        </div>
      </>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <>
        <Topbar title="Campaign" />
        <div className="flex-1 p-6">
          <ErrorState
            title="Couldn't load this campaign"
            onRetry={() => void detail.refetch()}
          />
        </div>
      </>
    );
  }

  const data = detail.data;
  const busy = launch.isPending || pause.isPending;
  const pctOfSent = (n: number) =>
    data.stats.sent > 0 ? (n / data.stats.sent) * 100 : 0;
  const openRate = pctOfSent(data.stats.opened);
  const clickRate = pctOfSent(data.stats.clicked);
  const replyRate = pctOfSent(data.stats.replied);
  const bounceRate = pctOfSent(data.stats.bounced);
  const completionRate =
    data.stats.total > 0 ? (data.stats.sent / data.stats.total) * 100 : 0;

  return (
    <>
      <Topbar
        title={data.name}
        description={
          <span className="flex items-center gap-2">
            <Link to="/campaigns" className="text-mb-text-muted hover:text-mb-text">
              <ArrowLeft className="h-3 w-3 inline mr-0.5" />
              All campaigns
            </Link>
            <span className="text-mb-text-subtle">·</span>
            <StatusPill status={data.status} />
          </span>
        }
        actions={
          <div className="flex gap-2">
            {(data.status === 'draft' || data.status === 'paused') && (
              <button
                className="mb-btn-primary"
                onClick={() => launch.mutate()}
                disabled={busy}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : data.status === 'paused' ? (
                  <Play className="h-4 w-4" />
                ) : (
                  <Rocket className="h-4 w-4" />
                )}
                {data.status === 'paused' ? 'Resume' : 'Launch'}
              </button>
            )}
            {data.status === 'running' && (
              <button
                className="mb-btn-secondary"
                onClick={() => pause.mutate()}
                disabled={busy}
              >
                <Pause className="h-4 w-4" />
                Pause
              </button>
            )}
          </div>
        }
      />

      <div className="flex-1 overflow-auto p-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard
            label="Completion"
            value={`${completionRate.toFixed(1)}%`}
            icon={Send}
            delta={{
              value: `${data.stats.sent.toLocaleString()} / ${data.stats.total.toLocaleString()}`,
              direction: 'flat',
            }}
          />
          <KpiCard label="Sent" value={data.stats.sent} icon={MailCheck} />
          <KpiCard
            label="Opened"
            value={data.stats.opened}
            icon={Eye}
            delta={{
              value: `${openRate.toFixed(1)}%`,
              direction: openRate > 0 ? 'up' : 'flat',
            }}
          />
          <KpiCard
            label="Clicked"
            value={data.stats.clicked}
            icon={MousePointerClick}
            delta={{
              value: `${clickRate.toFixed(1)}%`,
              direction: clickRate > 0 ? 'up' : 'flat',
            }}
          />
          <KpiCard
            label="Replied"
            value={data.stats.replied}
            icon={MessageSquareReply}
            delta={{
              value: `${replyRate.toFixed(1)}%`,
              direction: replyRate > 0 ? 'up' : 'flat',
            }}
          />
          <KpiCard
            label="Bounced"
            value={data.stats.bounced}
            icon={AlertTriangle}
            delta={{
              value: `${bounceRate.toFixed(1)}%`,
              direction: bounceRate > 3 ? 'down' : 'flat',
            }}
          />
        </div>

        <ReplyEngineCard
          campaignId={data.id}
          value={data.reply_engine}
          onChange={(engine) => {
            void campaignsApi
              .update(data.id, { reply_engine: engine })
              .then(() => qc.invalidateQueries({ queryKey: qk.campaigns.detail(data.id) }));
          }}
        />

        <ScheduleCard campaign={data} />

        <div className="mb-card p-5">
          <h2 className="font-display font-semibold text-mb-text mb-3">
            Email steps ({data.steps.length})
          </h2>
          <div className="space-y-3">
            {data.steps.map((s) => (
              <StepEditor
                key={s.id}
                campaignId={data.id}
                step={s}
                step1Subject={data.steps[0]?.subject_template ?? ''}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Editable view of one campaign step. Click Edit to switch to input
 * fields (RichTextEditor for the body, subject only on step 1, delay
 * for step 2+, plus attachments); Save PATCHes /campaigns/:id/steps/:id.
 * Cancel restores the previous values from `step`.
 */
function StepEditor({
  campaignId,
  step,
  step1Subject,
}: {
  campaignId: string;
  step: CampaignStep;
  step1Subject: string;
}): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(step.subject_template);
  const [body, setBody] = useState(step.body_html_template);
  const [delay, setDelay] = useState(step.delay_days_after_prev);
  const [attachments, setAttachments] = useState<AttachmentRow[]>(() =>
    step.attachment_ids.map((id) => ({
      id,
      filename: 'attachment',
      mime_type: 'application/octet-stream',
      size_bytes: 0,
      created_at: '',
    })),
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const isStep1 = step.step_number === 1;

  const save = useMutation({
    mutationFn: () =>
      campaignsApi.updateStep(campaignId, step.id, {
        subject_template: isStep1 ? subject : undefined,
        body_html_template: body,
        delay_days_after_prev: isStep1 ? undefined : delay,
        attachment_ids: attachments.map((a) => a.id),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.campaigns.detail(campaignId) });
      setEditing(false);
      setSaveError(null);
    },
    onError: (err) => {
      setSaveError(err instanceof ApiError ? err.message : 'Save failed.');
    },
  });

  function cancel(): void {
    setSubject(step.subject_template);
    setBody(step.body_html_template);
    setDelay(step.delay_days_after_prev);
    setAttachments(
      step.attachment_ids.map((id) => ({
        id,
        filename: 'attachment',
        mime_type: 'application/octet-stream',
        size_bytes: 0,
        created_at: '',
      })),
    );
    setSaveError(null);
    setEditing(false);
  }

  return (
    <div className="border border-mb-border rounded-md p-3">
      <div className="flex items-center gap-2 text-xs text-mb-text-muted mb-2">
        <span className="font-mono">Step {step.step_number}</span>
        {editing && !isStep1 ? (
          <span>
            · delay
            <input
              type="number"
              min={0}
              max={60}
              value={delay}
              onChange={(e) => setDelay(Number(e.target.value))}
              className="mx-1 w-14 rounded border border-mb-border px-1 text-mb-text"
            />
            days after previous
          </span>
        ) : step.delay_days_after_prev > 0 ? (
          <span>· {step.delay_days_after_prev}d after previous</span>
        ) : (
          <span>· sends first</span>
        )}
        {step.stop_on_reply ? <span>· stops on reply</span> : null}
        <div className="ml-auto flex gap-2">
          {editing ? (
            <>
              <button
                type="button"
                className="text-xs text-mb-text-muted hover:underline"
                onClick={cancel}
                disabled={save.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mb-btn-primary text-xs"
                onClick={() => save.mutate()}
                disabled={save.isPending}
              >
                {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                Save
              </button>
            </>
          ) : (
            <button
              type="button"
              className="text-xs text-mb-accent-press hover:underline"
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          {isStep1 ? (
            <input
              type="text"
              className="mb-input"
              placeholder="Subject line — {{first_name}}, {{company}} allowed"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          ) : (
            <div className="rounded border border-dashed border-mb-border bg-mb-bg-soft px-2.5 py-2 text-[11px] text-mb-text-muted">
              Threaded under step 1's subject:{' '}
              <b className="text-mb-text">Re: {step1Subject || '(step 1 not set)'}</b>
            </div>
          )}
          <RichTextEditor value={body} onChange={setBody} rows={5} />
          <AttachmentPicker attached={attachments} onChange={setAttachments} disabled={save.isPending} />
          {saveError ? (
            <div role="alert" className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
              {saveError}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="font-medium text-mb-text mb-1">
            {isStep1 ? step.subject_template : `Re: ${step1Subject}`}
          </div>
          <div
            className="text-xs text-mb-text-muted line-clamp-3 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: step.body_html_template }}
          />
          {step.attachment_ids.length > 0 ? (
            <div className="mt-1 text-[10px] text-mb-text-subtle">
              📎 {step.attachment_ids.length} attachment{step.attachment_ids.length === 1 ? '' : 's'}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}


function ReplyEngineCard({
  campaignId,
  value,
  onChange,
}: {
  campaignId: string;
  value: ReplyEngine;
  onChange: (engine: ReplyEngine) => void;
}): JSX.Element {
  const options: Array<{
    id: ReplyEngine;
    label: string;
    hint: string;
    icon: typeof Sparkles;
  }> = [
    {
      id: 'deepseek',
      label: 'Markdown Marty (LLM)',
      hint: 'DeepSeek-powered. Best for open-ended replies + nuanced tone. ~$0.14 / 1k tokens.',
      icon: Sparkles,
    },
    {
      id: 'mlm',
      label: 'MLM (deterministic)',
      hint: 'Keyword-matched markdown corpus. No API cost, ships provenance for every reply.',
      icon: BookOpen,
    },
  ];
  return (
    <div className="mb-card p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-semibold text-mb-text">Reply engine</h2>
        <span className="text-xs text-mb-text-subtle">Campaign #{campaignId.slice(0, 8)}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {options.map((opt) => {
          const active = opt.id === value;
          const Icon = opt.icon;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onChange(opt.id)}
              className={
                'text-left rounded-lg border p-3 transition-colors focus:outline-none focus:ring-2 focus:ring-mb-accent ' +
                (active
                  ? 'border-mb-accent bg-mb-accent-soft'
                  : 'border-mb-border hover:border-mb-text-muted bg-mb-surface')
              }
              aria-pressed={active}
            >
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-mb-accent-press" aria-hidden />
                <span className="font-medium text-mb-text">{opt.label}</span>
                {active ? (
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-mb-accent-press">
                    Active
                  </span>
                ) : null}
              </div>
              <div className="mt-1 text-xs text-mb-text-muted">{opt.hint}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ScheduleCard({ campaign }: { campaign: CampaignDetail }): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  // Convert the stored UTC ISO back to a wall-clock string in the campaign
  // timezone so the datetime-local input has a sensible starting value.
  function utcIsoToWallClock(iso: string | null, tz: string): string {
    if (!iso) return '';
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date(iso));
    const pick = (k: string) => parts.find((p) => p.type === k)?.value ?? '00';
    const hour = pick('hour') === '24' ? '00' : pick('hour');
    return `${pick('year')}-${pick('month')}-${pick('day')}T${hour}:${pick('minute')}`;
  }

  const [sendDays, setSendDays] = useState(campaign.send_days);
  const [sendWindowStart, setSendWindowStart] = useState(campaign.send_window_start);
  const [sendWindowEnd, setSendWindowEnd] = useState(campaign.send_window_end);
  const [timezone, setTimezone] = useState(campaign.timezone);
  const [startAt, setStartAt] = useState(
    utcIsoToWallClock(campaign.scheduled_start_at, campaign.timezone),
  );
  const [dailyLimit, setDailyLimit] = useState(
    campaign.daily_send_limit != null ? String(campaign.daily_send_limit) : '',
  );

  const save = useMutation({
    mutationFn: () =>
      campaignsApi.update(campaign.id, {
        send_days: sendDays,
        send_window_start: sendWindowStart,
        send_window_end: sendWindowEnd,
        timezone,
        scheduled_start_at: wallClockToUtcIso(startAt, timezone),
        daily_send_limit: dailyLimit.trim() ? Math.max(1, parseInt(dailyLimit, 10)) : null,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.campaigns.detail(campaign.id) });
      setEditing(false);
    },
  });

  function cancelEdit() {
    setSendDays(campaign.send_days);
    setSendWindowStart(campaign.send_window_start);
    setSendWindowEnd(campaign.send_window_end);
    setTimezone(campaign.timezone);
    setStartAt(utcIsoToWallClock(campaign.scheduled_start_at, campaign.timezone));
    setDailyLimit(campaign.daily_send_limit != null ? String(campaign.daily_send_limit) : '');
    setEditing(false);
  }

  const canSave =
    sendDays > 0 &&
    sendWindowStart < sendWindowEnd &&
    !save.isPending;

  return (
    <div className="mb-card p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-semibold text-mb-text flex items-center gap-2">
          <Clock className="h-4 w-4 text-mb-accent-press" />
          Schedule
        </h2>
        {editing ? (
          <div className="flex gap-2">
            <button
              type="button"
              className="mb-btn-secondary text-xs"
              onClick={cancelEdit}
              disabled={save.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mb-btn-primary text-xs"
              onClick={() => save.mutate()}
              disabled={!canSave}
            >
              {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              Save
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <RescheduleQueueButton campaignId={campaign.id} />
            <button
              type="button"
              className="text-xs text-mb-accent-press hover:underline"
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-mb-text uppercase tracking-wider mb-2">
              Days to send
            </label>
            <div className="flex flex-wrap gap-1.5">
              {DAY_LABELS.map((d) => {
                const on = (sendDays & d.bit) !== 0;
                return (
                  <button
                    key={d.bit}
                    type="button"
                    onClick={() => setSendDays(sendDays ^ d.bit)}
                    className={
                      'rounded-md border px-3 py-1.5 text-xs transition-colors ' +
                      (on
                        ? 'border-mb-accent bg-mb-accent-soft text-mb-accent-press font-medium'
                        : 'border-mb-border bg-mb-surface text-mb-text-muted hover:border-mb-text-muted')
                    }
                  >
                    {d.short}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Window starts">
              <input
                type="time"
                className="mb-input"
                value={sendWindowStart}
                onChange={(e) => setSendWindowStart(e.target.value)}
              />
            </Field>
            <Field label="Window ends">
              <input
                type="time"
                className="mb-input"
                value={sendWindowEnd}
                onChange={(e) => setSendWindowEnd(e.target.value)}
              />
            </Field>
          </div>

          <Field label="Timezone">
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="mb-input"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.id} value={tz.id}>
                  {tz.label}
                </option>
              ))}
              {TIMEZONES.every((tz) => tz.id !== timezone) ? (
                <option value={timezone}>{timezone}</option>
              ) : null}
            </select>
          </Field>

          <Field
            label="Start date & time"
            hint="Leave blank to start whenever the campaign is launched"
          >
            <input
              type="datetime-local"
              className="mb-input"
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
            />
          </Field>

          <Field
            label="Daily send limit"
            hint="Cap how many emails this campaign sends per UTC day. Blank = no campaign-level cap."
          >
            <input
              type="number"
              min={1}
              max={100000}
              placeholder="e.g. 100"
              className="mb-input"
              value={dailyLimit}
              onChange={(e) => setDailyLimit(e.target.value)}
            />
          </Field>

          {save.isError ? (
            <div role="alert" className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
              Couldn't save schedule. Try again.
            </div>
          ) : null}
        </div>
      ) : (
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-mb-text-muted">Days</dt>
          <dd className="text-mb-text">{daysLabel(campaign.send_days)}</dd>
          <dt className="text-mb-text-muted">Window</dt>
          <dd className="text-mb-text">
            {campaign.send_window_start} – {campaign.send_window_end}
          </dd>
          <dt className="text-mb-text-muted">Timezone</dt>
          <dd className="text-mb-text">{campaign.timezone}</dd>
          <dt className="text-mb-text-muted">Starts</dt>
          <dd className="text-mb-text">
            {campaign.scheduled_start_at
              ? `${formatInZone(campaign.scheduled_start_at, campaign.timezone)} (${campaign.timezone})`
              : 'Immediately on Launch'}
          </dd>
          <dt className="text-mb-text-muted">Daily cap</dt>
          <dd className="text-mb-text">
            {campaign.daily_send_limit != null
              ? `${campaign.daily_send_limit.toLocaleString()} sends / day`
              : 'No campaign-level cap'}
          </dd>
        </dl>
      )}
    </div>
  );
}

/**
 * "Reschedule queue" — rewrites scheduled_at on every currently-queued
 * message using the current daily cap + window + send days. Useful when
 * the cap changes or a legacy launch spread sends across weird hours.
 */
function RescheduleQueueButton({ campaignId }: { campaignId: string }): JSX.Element {
  const qc = useQueryClient();
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const reschedule = useMutation({
    mutationFn: () => campaignsApi.reschedule(campaignId),
    onSuccess: async (r) => {
      setErr(null);
      if (r.updated === 0) {
        setNote('No queued messages to reschedule.');
      } else {
        const first = r.first_send_at ? new Date(r.first_send_at).toLocaleString() : '—';
        setNote(`Rescheduled ${r.updated} messages. First send: ${first}.`);
      }
      await qc.invalidateQueries({ queryKey: qk.campaigns.detail(campaignId) });
    },
    onError: (e) => {
      setErr(e instanceof ApiError ? e.message : 'Reschedule failed.');
      setNote(null);
    },
  });

  return (
    <>
      <button
        type="button"
        className="text-xs text-mb-accent-press hover:underline"
        onClick={() => {
          if (
            window.confirm(
              'Reschedule all queued messages using the current daily cap + send window? '
              + 'This rewrites scheduled_at on unsent rows; nothing already sent is affected.',
            )
          ) {
            reschedule.mutate();
          }
        }}
        disabled={reschedule.isPending}
        title="Rewrite scheduled_at on every unsent message"
      >
        {reschedule.isPending ? 'Rescheduling…' : 'Reschedule queue'}
      </button>
      {note ? (
        <span className="ml-2 text-[11px] text-mb-success">{note}</span>
      ) : null}
      {err ? <span className="ml-2 text-[11px] text-mb-danger">{err}</span> : null}
    </>
  );
}
